import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import './App.css';
import Card from './components/Card';
import Modal from './components/Modal';
import Login from './components/Login';
import AdminPanel from './components/AdminPanel';
import StructureEditor from './components/StructureEditor';
import { io } from 'socket.io-client';

interface User {
  username: string;
  is_admin: number;
}

interface Profile {
  id: number;
  name: string;
  percentage?: number;
  theory_passed?: number;
}

interface Item {
  id: number;
  label: string;
  state: number;
}

interface Section {
  id: number;
  title: string;
  items: Item[];
}

const isProd = process.env.NODE_ENV === 'production';
const API_URL = isProd ? '/api' : `http://${window.location.hostname}:4658/api`;
const socket = io(isProd ? '/' : `http://${window.location.hostname}:4658`);

function App() {
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [user, setUser] = useState<User | null>(JSON.parse(localStorage.getItem('user') || 'null'));
  
  const [view, setView] = useState<'dashboard' | 'admin' | 'settings' | 'structure'>('dashboard');
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [activeProfile, setActiveProfile] = useState<Profile | null>(null);
  const [sections, setSections] = useState<Section[]>([]);
  const [newProfileName, setNewProfileName] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRange, setFilterRange] = useState<number | null>(null);
  const [theoryFilter, setTheoryFilter] = useState<'passed' | 'open' | null>(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(socket.connected);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0); // 0 = oben, 1 = voll gescrollt
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalClosing, setIsModalClosing] = useState(false);
  const [profileToDelete, setProfileToDelete] = useState<number | null>(null);

  const closeModal = () => {
    setIsModalClosing(true);
    setTimeout(() => {
      setIsModalOpen(false);
      setIsModalClosing(false);
      setProfileToDelete(null);
    }, 300);
  };

  const toggleMenu = () => {
    if (isMenuOpen) {
      setIsClosing(true);
      setTimeout(() => {
        setIsMenuOpen(false);
        setIsClosing(false);
      }, 300); // Dauer der Animation
    } else {
      setIsMenuOpen(true);
    }
  };

  const handleMenuAction = (action: () => void) => {
    setIsClosing(true);
    setTimeout(() => {
      action();
      setIsMenuOpen(false);
      setIsClosing(false);
    }, 300);
  };

  useEffect(() => {
    const handleScroll = () => {
      const maxScroll = 150; // Nach 150px ist die Animation abgeschlossen
      const progress = Math.min(window.scrollY / maxScroll, 1);
      setScrollProgress(progress);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = useCallback(() => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setView('dashboard');
    setActiveProfile(null);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const showError = useCallback((msg: string) => {
    setError(msg);
    setTimeout(() => setError(null), 3000);
  }, []);

  const fetchProfiles = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/profiles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfiles(response.data);
      setLoading(false);
    } catch (err: any) {
      setLoading(false);
      if (err.response?.status === 401 || err.response?.status === 403) {
        handleLogout();
      } else {
        showError('Verbindung zum Server fehlgeschlagen');
      }
    }
  }, [token, handleLogout, showError]);

  useEffect(() => {
    if (token) {
      fetchProfiles();
    }
  }, [token, fetchProfiles]);

  useEffect(() => {
    const onConnect = () => setIsConnected(true);
    const onDisconnect = () => setIsConnected(false);

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);

    socket.on('progress_updated', (data) => {
      // Update local view if profile is open
      if (activeProfile && activeProfile.id === data.profileId) {
        setSections(prev => prev.map(section => ({
          ...section,
          items: section.items.map(item => 
            item.id === data.templateId ? { ...item, state: data.state } : item
          )
        })));
      }
      // Refresh profiles list to update percentages in dashboard
      fetchProfiles();
    });

    socket.on('profiles_updated', () => {
      fetchProfiles();
    });

    socket.on('structure_updated', () => {
      if (activeProfile) {
        fetchProfileData(activeProfile);
      }
    });

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('progress_updated');
      socket.off('profiles_updated');
      socket.off('structure_updated');
    };
  }, [activeProfile, fetchProfiles]);

  const fetchProfileData = async (profile: Profile) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/profiles/${profile.id}/data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSections(response.data.sections);
      setActiveProfile(response.data.profile);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      showError('Daten konnten nicht geladen werden');
    }
  };

  const handleToggleTheory = async () => {
    if (!activeProfile) return;
    const newState = activeProfile.theory_passed ? 0 : 1;
    try {
      await axios.post(`${API_URL}/profiles/${activeProfile.id}/theory`, 
        { theoryPassed: !!newState },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setActiveProfile({ ...activeProfile, theory_passed: newState });
    } catch (err) {
      showError('Fehler beim Speichern');
    }
  };

  const handleCreateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newProfileName.trim()) return;
    try {
      const response = await axios.post(`${API_URL}/profiles`, { name: newProfileName }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfiles(prev => [...prev, response.data].sort((a, b) => a.name.localeCompare(b.name)));
      setNewProfileName('');
    } catch (err) {
      showError('Fehler beim Erstellen');
    }
  };

  const openDeleteModal = (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    setProfileToDelete(id);
    setIsModalOpen(true);
  };

  const confirmDeleteProfile = async () => {
    if (profileToDelete === null) return;
    try {
      await axios.delete(`${API_URL}/profiles/${profileToDelete}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfiles(profiles.filter(p => p.id !== profileToDelete));
      if (activeProfile?.id === profileToDelete) setActiveProfile(null);
      closeModal();
    } catch (err) {
      showError('Fehler beim Löschen');
      closeModal();
    }
  };

  const handleUpdateProgress = async (templateId: number, newState: number) => {
    if (!activeProfile) return;
    try {
      await axios.post(`${API_URL}/progress`, {
        profileId: activeProfile.id,
        templateId,
        state: newState
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSections(prev => prev.map(section => ({
        ...section,
        items: section.items.map(item => 
          item.id === templateId ? { ...item, state: newState } : item
        )
      })));
    } catch (err) {
      showError('Fehler: Keine Verbindung zur Datenbank!');
      throw err;
    }
  };

  const handleRenameProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeProfile || !editedName.trim()) return;
    try {
      await axios.put(`${API_URL}/profiles/${activeProfile.id}`, { name: editedName }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActiveProfile({ ...activeProfile, name: editedName });
      setProfiles(prev => prev.map(p => p.id === activeProfile.id ? { ...p, name: editedName } : p)
        .sort((a, b) => a.name.localeCompare(b.name)));
      setIsEditingName(false);
    } catch (err) {
      showError('Fehler beim Umbenennen');
    }
  };

  const handleLogin = (newToken: string, newUser: User) => {
    setToken(newToken);
    setUser(newUser);
    localStorage.setItem('token', newToken);
    localStorage.setItem('user', JSON.stringify(newUser));
  };

  const handleChangePassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const newPass = (form.elements.namedItem('newPassword') as HTMLInputElement).value;
    try {
      await axios.post(`${API_URL}/change-password`, { newPassword: newPass }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Passwort erfolgreich geändert!');
      form.reset();
      setView('dashboard');
    } catch (err) {
      showError('Fehler beim Ändern des Passworts');
    }
  };

  const calculateCurrentPercentage = () => {
    let totalItems = 0;
    let totalScore = 0;
    sections.forEach(s => {
      s.items.forEach(i => {
        totalItems++;
        totalScore += i.state;
      });
    });
    return totalItems > 0 ? Math.round((totalScore / (totalItems * 4)) * 100) : 0;
  };

  if (!token || !user) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <div className="app-container">
      {isMenuOpen && <div className={`menu-overlay ${isClosing ? 'closing' : ''}`} onClick={toggleMenu} />}
      
      {!isConnected && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0,0,0,0.85)',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          color: 'white',
          padding: '20px'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '20px' }}>⚠️</div>
          <h2 style={{ marginBottom: '10px' }}>Verbindung unterbrochen</h2>
          <p style={{ color: 'var(--text-secondary)' }}>
            Die Verbindung zum Server wurde verloren.<br/>
            Bitte warte, während wir versuchen, sie wiederherzustellen...
          </p>
        </div>
      )}
      <div 
        className={`header ${scrollProgress > 0.1 ? 'scrolled-shadow' : ''}`}
        style={{
          padding: `${15 - (scrollProgress * 5)}px 20px`,
          height: `${120 - (scrollProgress * 60)}px`, // Kompakterer Header
          justifyContent: 'center'
        }}
      >
        <img 
          src={`${process.env.PUBLIC_URL}/vahrschule logo.png`} 
          alt="Vahrschule Logo" 
          className="logo-large"
          onClick={scrollToTop}
          style={{
            width: 'auto',
            maxWidth: `${280 - (scrollProgress * 180)}px`, // Von 280px auf 100px
            transition: 'none',
            cursor: 'pointer'
          }}
        />
        <div 
          className="hamburger-menu"
          style={{
            top: '50%',
            transform: 'translateY(-50%)',
            right: '20px'
          }}
        >
          <button 
            className="hamburger-icon" 
            onClick={toggleMenu}
            style={{
              width: `${44 - (scrollProgress * 6)}px`,
              height: `${44 - (scrollProgress * 6)}px`,
              fontSize: `${1.8 - (scrollProgress * 0.3)}rem`,
              backgroundColor: isMenuOpen ? 'var(--accent-blue)' : ''
            }}
          >
            {isMenuOpen ? '✕' : '☰'}
          </button>
          {isMenuOpen && (
            <div className={`nav-dropdown ${isClosing ? 'closing' : ''}`} style={{ top: '100%' }}>
              <button className="btn btn-secondary" onClick={() => handleMenuAction(() => { setView('dashboard'); setActiveProfile(null); })}>🏠 Dashboard</button>
              <button className="btn btn-secondary" onClick={() => handleMenuAction(() => setView('settings'))}>⚙️ Einstellungen</button>
              {user.is_admin === 1 && (
                <>
                  <button className="btn btn-secondary" onClick={() => handleMenuAction(() => setView('structure'))}>🏗️ Karten Aufbau</button>
                  <button className="btn btn-secondary" onClick={() => handleMenuAction(() => setView('admin'))}>👤 Admin</button>
                </>
              )}
              <div style={{ margin: '5px 0', borderTop: '1px solid rgba(255,255,255,0.1)' }} />
              <button className="btn btn-danger" onClick={() => handleMenuAction(handleLogout)}>🚪 Logout ({user.username})</button>
            </div>
          )}
        </div>
      </div>

      <div className="content-padding" style={{ marginTop: `-${scrollProgress * 40}px` }}>
        {loading && <div className="loading" style={{ position: 'fixed', top: '10px', right: '10px' }}>Lädt...</div>}

        {view === 'structure' && user.is_admin === 1 && <StructureEditor token={token!} />}

        {view === 'dashboard' && (
          <>
            {!activeProfile ? (
              <div className="profile-list">
                {user.is_admin === 1 && (
                  <div className="add-card-form">
                    <h3>Neues Schüler-Profil</h3>
                    <form onSubmit={handleCreateProfile} style={{ display: 'flex', gap: '10px' }}>
                      <input 
                        className="input-field"
                        style={{ marginBottom: 0 }}
                        placeholder="Name des Schülers" 
                        value={newProfileName}
                        onChange={(e) => setNewProfileName(e.target.value)}
                      />
                      <button type="submit" className="btn btn-primary">Erstellen</button>
                    </form>
                  </div>
                )}

                <h3>Schüler Übersicht:</h3>
                <input 
                  className="input-field"
                  placeholder="🔍 Schüler suchen..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ marginBottom: '20px' }}
                />

                {/* STATISTIKEN ANFANG */}
                {(() => {
                  const filteredBySearch = profiles.filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()));
                  const counts = [
                    { label: '0-25%', count: filteredBySearch.filter(p => (p.percentage || 0) <= 25).length, perc: 25 },
                    { label: '25-50%', count: filteredBySearch.filter(p => (p.percentage || 0) > 25 && (p.percentage || 0) <= 50).length, perc: 50 },
                    { label: '50-75%', count: filteredBySearch.filter(p => (p.percentage || 0) > 50 && (p.percentage || 0) <= 75).length, perc: 75 },
                    { label: '75-100%', count: filteredBySearch.filter(p => (p.percentage || 0) > 75).length, perc: 100 }
                  ];

                  return (
                    <div className="stats-container">
                      <div className="stats-total">
                        Insgesamt: {filteredBySearch.length} Schüler
                        {(filterRange !== null || theoryFilter !== null) && (
                          <span 
                            style={{ marginLeft: '10px', color: 'var(--accent-blue)', cursor: 'pointer', textDecoration: 'underline' }}
                            onClick={() => { setFilterRange(null); setTheoryFilter(null); }}
                          >
                            (Filter aufheben)
                          </span>
                        )}
                      </div>
                      <div className="stats-rings">
                        {counts.map((stat, i) => (
                          <div 
                            key={i} 
                            className={`stat-item ${filterRange === i ? 'active' : ''}`}
                            onClick={() => setFilterRange(filterRange === i ? null : i)}
                          >
                            <div style={{ position: 'relative', width: '46px', height: '46px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                              <svg width="46" height="46" style={{ transform: 'rotate(-90deg)' }}>
                                <circle cx="23" cy="23" r="19" stroke="rgba(255,255,255,0.05)" strokeWidth="3" fill="transparent" />
                                <circle
                                  cx="23"
                                  cy="23"
                                  r="19"
                                  stroke={`hsl(${stat.perc * 1.2}, 70%, 45%)`}
                                  strokeWidth="3"
                                  fill="transparent"
                                  strokeDasharray={2 * Math.PI * 19}
                                  strokeDashoffset={(2 * Math.PI * 19) - (stat.perc / 100) * (2 * Math.PI * 19)}
                                  strokeLinecap="round"
                                  style={{ transition: 'stroke-dashoffset 0.8s ease' }}
                                />
                              </svg>
                              <span className="stat-ring-inner">{stat.count}</span>
                            </div>
                            <span className="stat-label">{stat.label}</span>
                          </div>
                        ))}
                      </div>

                      <div className="stats-rings" style={{ marginTop: '15px', justifyContent: 'center', gap: '40px' }}>
                        <div 
                          className={`stat-item ${theoryFilter === 'passed' ? 'active' : ''}`}
                          onClick={() => setTheoryFilter(theoryFilter === 'passed' ? null : 'passed')}
                          style={{ flex: 'none', width: '60px' }}
                        >
                          <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            backgroundColor: 'var(--state-green)',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            fontSize: '1.2rem',
                            fontWeight: 'bold',
                            color: 'white',
                            position: 'relative'
                          }}>
                            ✓
                            <span style={{ 
                              position: 'absolute', 
                              bottom: '-5px', 
                              right: '-5px', 
                              backgroundColor: 'var(--card-bg)', 
                              fontSize: '0.7rem', 
                              padding: '2px 5px', 
                              borderRadius: '10px',
                              border: '1px solid rgba(255,255,255,0.1)'
                            }}>
                              {filteredBySearch.filter(p => p.theory_passed === 1).length}
                            </span>
                          </div>
                          <span className="stat-label">Theorie<br/>Bestanden</span>
                        </div>

                        <div 
                          className={`stat-item ${theoryFilter === 'open' ? 'active' : ''}`}
                          onClick={() => setTheoryFilter(theoryFilter === 'open' ? null : 'open')}
                          style={{ flex: 'none', width: '60px' }}
                        >
                          <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            backgroundColor: 'var(--state-red)',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            fontSize: '1.2rem',
                            fontWeight: 'bold',
                            color: 'white',
                            position: 'relative'
                          }}>
                            ✕
                            <span style={{ 
                              position: 'absolute', 
                              bottom: '-5px', 
                              right: '-5px', 
                              backgroundColor: 'var(--card-bg)', 
                              fontSize: '0.7rem', 
                              padding: '2px 5px', 
                              borderRadius: '10px',
                              border: '1px solid rgba(255,255,255,0.1)'
                            }}>
                              {filteredBySearch.filter(p => p.theory_passed === 0).length}
                            </span>
                          </div>
                          <span className="stat-label">Theorie<br/>Offen</span>
                        </div>
                      </div>
                    </div>
                  );
                })()}
                {/* STATISTIKEN ENDE */}

                {profiles
                  .filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()))
                  .filter(p => {
                    if (filterRange === null) return true;
                    const prc = p.percentage || 0;
                    if (filterRange === 0) return prc <= 25;
                    if (filterRange === 1) return prc > 25 && prc <= 50;
                    if (filterRange === 2) return prc > 50 && prc <= 75;
                    if (filterRange === 3) return prc > 75;
                    return true;
                  })
                  .filter(p => {
                    if (theoryFilter === null) return true;
                    if (theoryFilter === 'passed') return p.theory_passed === 1;
                    if (theoryFilter === 'open') return p.theory_passed === 0;
                    return true;
                  })
                  .map(p => {
                    const percentage = p.percentage ?? 0;
                    // Calculate color: Red (0, 200, 80) to Green (120, 200, 80) in HSL
                    const hue = (percentage * 1.2); // 0% = 0 (red), 100% = 120 (green)
                    const color = `hsl(${hue}, 70%, 45%)`;
                    const radius = 18;
                    const circumference = 2 * Math.PI * radius;
                    const offset = circumference - (percentage / 100) * circumference;

                    return (
                      <div key={p.id} className="dashboard-card" onClick={() => { fetchProfileData(p); window.scrollTo(0, 0); }} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                          <div style={{ position: 'relative', width: '54px', height: '54px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <svg width="54" height="54" style={{ transform: 'rotate(-90deg)' }}>
                              <circle
                                cx="27"
                                cy="27"
                                r="23"
                                stroke="#eee"
                                strokeWidth="4"
                                fill="transparent"
                              />
                              <circle
                                cx="27"
                                cy="27"
                                r="23"
                                stroke={color}
                                strokeWidth="4"
                                fill="transparent"
                                strokeDasharray={2 * Math.PI * 23}
                                strokeDashoffset={(2 * Math.PI * 23) - (percentage / 100) * (2 * Math.PI * 23)}
                                strokeLinecap="round"
                                style={{ transition: 'stroke-dashoffset 0.5s ease, stroke 0.5s ease' }}
                              />
                            </svg>
                            <span style={{ 
                              position: 'absolute', 
                              fontSize: '0.85rem', 
                              fontWeight: 'bold',
                              color: 'white' 
                            }}>
                              {percentage}%
                            </span>
                          </div>
                          <span>{p.name}</span>
                        </div>
                        {user.is_admin === 1 && (
                          <button 
                            className="btn btn-danger btn-small" 
                            onClick={(e) => openDeleteModal(e, p.id)}
                            style={{ minWidth: '30px' }}
                          >
                            X
                          </button>
                        )}
                      </div>
                    );
                  })}            </div>
            ) : (
              <div className="profile-detail">
                <div className="nav-back" onClick={() => setActiveProfile(null)}>🔙 Zurück zur Übersicht</div>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '15px', marginBottom: '30px' }}>
                  {isEditingName ? (
                    <form onSubmit={handleRenameProfile} style={{ display: 'flex', gap: '10px' }}>
                      <input 
                        className="input-field" 
                        style={{ marginBottom: 0, fontSize: '1.2rem', textAlign: 'center' }}
                        value={editedName}
                        onChange={(e) => setEditedName(e.target.value)}
                        autoFocus
                      />
                      <button type="submit" className="btn btn-primary btn-small">✓</button>
                      <button type="button" className="btn btn-secondary btn-small" onClick={() => setIsEditingName(false)}>X</button>
                    </form>
                  ) : (
                    <>
                      <div style={{ position: 'relative', width: '54px', height: '54px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <svg width="54" height="54" style={{ transform: 'rotate(-90deg)' }}>
                          <circle cx="27" cy="27" r="23" stroke="#eee" strokeWidth="4" fill="transparent" />
                          <circle
                            cx="27"
                            cy="27"
                            r="23"
                            stroke={`hsl(${calculateCurrentPercentage() * 1.2}, 70%, 45%)`}
                            strokeWidth="4"
                            fill="transparent"
                            strokeDasharray={2 * Math.PI * 23}
                            strokeDashoffset={(2 * Math.PI * 23) - (calculateCurrentPercentage() / 100) * (2 * Math.PI * 23)}
                            strokeLinecap="round"
                            style={{ transition: 'stroke-dashoffset 0.5s ease, stroke 0.5s ease' }}
                          />
                        </svg>
                        <span style={{ position: 'absolute', fontSize: '0.85rem', fontWeight: 'bold', color: 'white' }}>
                          {calculateCurrentPercentage()}%
                        </span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px' }}>
                        <h2 style={{ margin: 0 }}>{activeProfile.name}</h2>
                        <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                          <strong>{sections.reduce((acc, s) => acc + s.items.filter(i => i.state === 4).length, 0)}</strong> von {sections.reduce((acc, s) => acc + s.items.length, 0)} Übungen abgeschlossen
                        </p>
                        <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                          <strong>{sections.reduce((acc, s) => acc + s.items.filter(i => i.state > 0 && i.state < 4).length, 0)}</strong> Übungen in Bearbeitung
                        </p>
                        </div>
                        {user.is_admin === 1 && (
                        <button 
                          className="btn btn-secondary btn-small" 
                          style={{ fontSize: '1rem', padding: '5px 8px' }}
                          onClick={() => { setEditedName(activeProfile.name); setIsEditingName(true); }}
                        >
                          📝
                        </button>
                        )}
                        </>
                        )}
                        </div>

                        <div 
                          className={`theory-status ${activeProfile.theory_passed ? 'passed' : 'failed'}`}
                          onClick={handleToggleTheory}
                        >
                          <div style={{
                            width: '18px',
                            height: '18px',
                            borderRadius: '50%',
                            border: `2px solid ${activeProfile.theory_passed ? 'var(--state-green)' : 'var(--state-red)'}`,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            backgroundColor: activeProfile.theory_passed ? 'var(--state-green)' : 'var(--state-red)'
                          }}>
                            {activeProfile.theory_passed ? (
                              <span style={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>✓</span>
                            ) : (
                              <span style={{ color: 'white', fontSize: '10px', fontWeight: 'bold' }}>✕</span>
                            )}
                          </div>
                          <span style={{ fontSize: '0.9rem', color: 'var(--text-primary)' }}>
                            Theorieprüfung {activeProfile.theory_passed ? 'bestanden' : 'offen'}
                          </span>
                        </div>
                        {sections.map(section => (

                  <Card 
                    key={section.id}
                    title={section.title}
                    items={section.items}
                    onUpdate={handleUpdateProgress}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {view === 'admin' && user.is_admin === 1 && <AdminPanel token={token} />}

        {view === 'settings' && (
          <div className="dashboard-card" style={{ maxWidth: '400px', margin: '30px auto' }}>
            <h3>Mein Profil / Einstellungen</h3>
            <p>Eingeloggt als: <strong>{user.username}</strong></p>
            <form onSubmit={handleChangePassword}>
              <div style={{ marginBottom: '15px' }}>
                <label>Neues Passwort</label>
                <input name="newPassword" type="password" className="input-field" required />
              </div>
              <button type="submit" className="btn btn-primary" style={{ width: '100%' }}>Passwort ändern</button>
            </form>
          </div>
        )}

        {isModalOpen && (
          <Modal 
            message={
              <>
                Bist du sicher, dass du <br/>
                <strong>{profiles.find(p => p.id === profileToDelete)?.name}</strong> <br/>
                löschen möchtest?
              </>
            }
            onConfirm={confirmDeleteProfile}
            onCancel={closeModal}
            isClosing={isModalClosing}
          />
        )}
        {error && <div className="error-toast">{error}</div>}
      </div>
    </div>
  );
}

export default App;
